import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Radar, CalendarDays, KanbanSquare, CheckCircle2,
  BarChart3, Radio, ListChecks, Handshake, Smartphone, BookOpen, Settings,
  History, Clapperboard, Palette, MessageCircle, FolderOpen, Zap, PenTool, Sparkles,
  Users, BookOpenCheck, Receipt,
  ChevronDown,
} from "lucide-react";
import { useAccount } from "@/context/AccountContext";
import { useAuth } from "@/context/AuthContext";
import { useChatUnread } from "@/lib/chat";

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard", creator: true, color: "#007AFF" },
  { to: "/chat", label: "Chat", icon: MessageCircle, testid: "nav-chat", creator: true, color: "#34C759" },
  { to: "/themenradar", label: "Themenradar", icon: Radar, testid: "nav-themenradar", creator: true, color: "#FF9500" },
  { to: "/event-radar", label: "Event-Radar", icon: Radio, testid: "nav-event-radar", creator: true, color: "#FF2D55" },
  { to: "/partner-radar", label: "Partner-Radar", icon: Handshake, testid: "nav-partner-radar", creator: false, color: "#5856D6" },
  { to: "/pipeline", label: "Content-Pipeline", icon: KanbanSquare, testid: "nav-pipeline", creator: true, color: "#AF52DE" },
  { to: "/brand-hub", label: "Brand Hub", icon: Palette, testid: "nav-brand-hub", creator: true, color: "#FF3B30" },
  { to: "/design-auftraege", label: "Design-Aufträge", icon: PenTool, testid: "nav-design-auftraege", creator: true, color: "#BF5AF2" },
  { to: "/design-anfrage-stellen", label: "Insta-Post Studio", icon: Sparkles, testid: "nav-design-anfrage", creator: true, color: "#64D2FF" },
  { to: "/dateien", label: "Dateien", icon: FolderOpen, testid: "nav-dateien", creator: true, color: "#FFCC00" },
  { to: "/creator-aufgaben", label: "Creator-Aufgaben", icon: ListChecks, testid: "nav-creator-aufgaben", creator: true, color: "#30B0C7" },
  { to: "/freigabe", label: "Freigabe-Center", icon: CheckCircle2, testid: "nav-freigabe", creator: true, color: "#32ADE6" },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, testid: "nav-kalender", creator: true, color: "#FF9F0A" },
  { to: "/performance", label: "Performance", icon: BarChart3, testid: "nav-performance", creator: false, color: "#00C7BE" },
  { to: "/posting-history", label: "Posting-History", icon: History, testid: "nav-posting-history", creator: false, color: "#8E8E93" },
  { to: "/dm-automation", label: "DM-Automation", icon: Zap, testid: "nav-dm-automation", creator: false, color: "#A2845E" },
  { to: "/kundenakten", label: "Kundenakten", icon: Users, testid: "nav-kundenakten", creator: false, color: "#0A84FF" },
  { to: "/buchungen", label: "Buchungen", icon: BookOpenCheck, testid: "nav-buchungen", creator: false, color: "#34C759" },
  { to: "/abrechnung", label: "Abrechnung", icon: Receipt, testid: "nav-abrechnung", creator: false, color: "#FF9500" },
];

function NavIconTile({ icon: Icon, color }) {
  return (
    <span
      className="h-8 w-8 rounded-[10px] flex items-center justify-center text-white shrink-0 shadow-[0_5px_12px_rgba(15,23,42,0.12)] transition-transform duration-150 group-hover:scale-105 group-active:scale-95"
      style={{ background: color }}
    >
      <Icon className="h-[18px] w-[18px]" />
    </span>
  );
}

const PRIMARY_PATHS = ["/", "/chat", "/themenradar", "/pipeline", "/freigabe", "/kalender", "/performance"];

function NavigationItem({ item, chatUnread, approvalsCount, onClose }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClose}
      data-testid={item.testid}
      className={({ isActive }) =>
        `group flex min-h-12 items-center gap-3 px-3 py-2 rounded-[15px] text-[14px] font-semibold transition-all duration-200 ${
          isActive
            ? "bg-[#087BFF] text-white shadow-[0_10px_24px_rgba(0,122,255,0.24)]"
            : "text-slate-700 hover:bg-white/70 hover:text-slate-950"
        }`
      }
    >
      <NavIconTile icon={item.icon} color={item.color} />
      <span className="flex-1 truncate">{item.label}</span>
      {item.to === "/chat" && chatUnread > 0 && (
        <span data-testid="nav-chat-badge" className="h-5 min-w-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
          {chatUnread > 99 ? "99+" : chatUnread}
        </span>
      )}
      {item.to === "/freigabe" && approvalsCount > 0 && (
        <span
          data-testid="nav-approvals-badge"
          aria-label={`${approvalsCount} offene Freigaben`}
          className="h-5 min-w-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
        >
          {approvalsCount > 99 ? "99+" : approvalsCount}
        </span>
      )}
    </NavLink>
  );
}

export function Sidebar({ approvalsCount = 0, mobileOpen = false, onClose = () => {} }) {
  const { current } = useAccount();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const chatUnread = useChatUnread(location.pathname === "/chat");
  const isCreator = user?.role === "creator";
  const [creatorPaths, setCreatorPaths] = useState(null);
  useEffect(() => {
    if (!isCreator) return;
    api.get("/settings/creator-nav").then(({ data }) => setCreatorPaths(data.paths)).catch(() => {});
  }, [isCreator]);
  const navItems = NAV.filter((item) => {
    if (!isCreator) return true;
    if (Array.isArray(creatorPaths)) return creatorPaths.includes(item.to) || item.to === "/";
    return item.creator;
  });
  const primaryItems = PRIMARY_PATHS.map((path) => navItems.find((item) => item.to === path)).filter(Boolean);
  const moreItems = navItems.filter((item) => !PRIMARY_PATHS.includes(item.to));
  const secondaryPaths = [...moreItems.map((item) => item.to), "/companion", "/handbuch", "/einstellungen"];
  const [moreOpen, setMoreOpen] = useState(() => secondaryPaths.some((path) => location.pathname.startsWith(path)));

  useEffect(() => {
    if (secondaryPaths.some((path) => location.pathname.startsWith(path))) setMoreOpen(true);
    // secondaryPaths is derived from the current navigation configuration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navItems.length]);

  return (
    <>
      {mobileOpen && (
        <div
          data-testid="mobile-nav-backdrop"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`liquid-sidebar no-print w-[min(88vw,292px)] lg:w-[260px] h-[calc(100dvh-24px)] m-3 flex flex-col fixed top-0 left-0 z-50 pt-[env(safe-area-inset-top)] transition-transform duration-300 ease-out lg:sticky lg:top-3 lg:shrink-0 lg:translate-x-0 lg:transition-none ${
          mobileOpen ? "translate-x-0" : "-translate-x-[calc(100%+24px)]"
        }`}
      >
      <button
        data-testid="sidebar-logo"
        onClick={() => { navigate("/"); onClose(); }}
        className="flex items-center gap-3 px-5 h-[76px] text-left shrink-0"
      >
        <img src="/innovationshafen-favicon-orange.png" alt="Innovationshafen" className="h-10 w-10 rounded-xl shadow-sm" />
        <div className="leading-tight">
          <div className="font-head font-bold text-[17px] text-slate-950">SM-Regio</div>
          <div className="text-xs text-slate-500 mt-0.5">KI-System</div>
        </div>
      </button>

      <nav className="flex-1 overflow-y-auto py-3 px-3.5 liquid-scroll">
        <p className="px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2.5">
          Hauptnavigation
        </p>
        <div className="space-y-1.5">
          {primaryItems.map((item) => <NavigationItem key={item.to} item={item} chatUnread={chatUnread} approvalsCount={approvalsCount} onClose={onClose} />)}
        </div>

        <button
          type="button"
          data-testid="sidebar-more-toggle"
          onClick={() => setMoreOpen((open) => !open)}
          aria-expanded={moreOpen}
          className="w-full px-3 mt-7 mb-2.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-700 transition-colors"
        >
          Mehr
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
        </button>
        {moreOpen && <div className="space-y-1.5 animate-rise">
          {moreItems.map((item) => <NavigationItem key={item.to} item={item} chatUnread={chatUnread} approvalsCount={approvalsCount} onClose={onClose} />)}
          <NavLink
            to="/companion"
            onClick={onClose}
            data-testid="nav-companion"
            className="group flex min-h-12 items-center gap-3 px-3 py-2 rounded-[15px] text-[14px] font-semibold text-slate-700 hover:bg-white/70 hover:text-slate-950 transition-all"
          >
            <NavIconTile icon={Smartphone} color="#98989D" />
            Mobile-Companion
          </NavLink>
          <NavLink
            to="/handbuch"
            onClick={onClose}
            data-testid="nav-handbuch"
            className={({ isActive }) =>
              `group flex min-h-12 items-center gap-3 px-3 py-2 rounded-[15px] text-[14px] font-semibold transition-all ${
                isActive ? "bg-[#087BFF] text-white shadow-[0_10px_24px_rgba(0,122,255,0.24)]" : "text-slate-700 hover:bg-white/70 hover:text-slate-950"
              }`
            }
          >
            <NavIconTile icon={BookOpen} color="#FF6482" />
            Handbuch
          </NavLink>
          <NavLink
            to="/einstellungen"
            onClick={onClose}
            data-testid="nav-einstellungen"
            className={({ isActive }) =>
              `group flex min-h-12 items-center gap-3 px-3 py-2 rounded-[15px] text-[14px] font-semibold transition-all ${
                isActive ? "bg-[#087BFF] text-white shadow-[0_10px_24px_rgba(0,122,255,0.24)]" : "text-slate-700 hover:bg-white/70 hover:text-slate-950"
              }`
            }
          >
            <NavIconTile icon={Settings} color="#8E8E93" />
            Einstellungen
          </NavLink>
        </div>}

        <div className="px-3 mt-8 mb-3">
          <p className="text-[10px] text-neutral-400 mb-2">Eine Initiative von</p>
          <a
            href="https://innovationshafen.net"
            target="_blank"
            rel="noreferrer"
            data-testid="sidebar-innovationshafen-logo"
            className="inline-block opacity-80 hover:opacity-100 transition-opacity"
          >
            <img src="/innovationshafen-logo.png" alt="Innovationshafen" className="h-5 w-auto" />
          </a>
        </div>
      </nav>

      {current && (
        <div className="p-3.5 pt-2">
          <div className="rounded-[18px] bg-white/55 border border-white/80 p-3.5 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: current.accent }} />
              <span className="text-xs font-semibold text-neutral-700 truncate">{current.name}</span>
            </div>
            <p className="text-[11px] text-neutral-400 mt-1 leading-snug">{current.tagline}</p>
          </div>
        </div>
      )}
    </aside>
    </>
  );
}
